import {
  prepareSingleWitnessCoreferenceChainExtractionWindowCalls,
  prepareSingleWitnessCoreferenceChainV2ExtractionWindowCalls,
  prepareSingleWitnessCoreferenceChainExtractionCall,
  prepareSingleWitnessFocalizationCharacterizationExtractionCall,
  prepareSingleWitnessCharacterExtractionCall,
  validatePreparedCoreferenceChainExtractionCall,
  validatePreparedCoreferenceChainV2ExtractionCall,
  validatePreparedFocalizationCharacterizationExtractionCall,
  validatePreparedExtractionCall,
  type CharacterExtractionScope,
  type CharacterIndexInput,
  type PreparedCharacterExtractionValidationIssue,
  type PreparedSingleWitnessCoreferenceChainExtractionCall,
  type PreparedSingleWitnessCoreferenceChainV2WindowCall,
  type PreparedSingleWitnessCoreferenceChainWindowCall,
  type PreparedSingleWitnessFocalizationCharacterizationExtractionCall,
  type PreparedSingleWitnessCharacterExtractionCall,
  type SingleWitnessCoreferenceChainV2Figure,
} from '@variorum/character-index'
import {
  buildCharacterIndexInput,
  type BuildCharacterIndexInputArgs,
} from './characterIndexPackageAdapter'

export interface PrepareLedgerSafeSingleWitnessCharacterExtractionArgs extends BuildCharacterIndexInputArgs {
  scope: CharacterExtractionScope
}

export interface PrepareLedgerSafeSingleWitnessFocalizationCharacterizationExtractionArgs
  extends PrepareLedgerSafeSingleWitnessCharacterExtractionArgs {
  figures: readonly SingleWitnessCoreferenceChainV2Figure[]
  focusFigureId?: string | null
}

export interface PreparedLedgerSafeSingleWitnessCharacterExtraction {
  input: CharacterIndexInput
  call: PreparedSingleWitnessCharacterExtractionCall
  validationIssues: PreparedCharacterExtractionValidationIssue[]
  isValid: boolean
}

export interface PreparedLedgerSafeSingleWitnessCoreferenceChainExtraction {
  input: CharacterIndexInput
  call: PreparedSingleWitnessCoreferenceChainExtractionCall
  validationIssues: PreparedCharacterExtractionValidationIssue[]
  isValid: boolean
}

export interface PreparedLedgerSafeSingleWitnessCoreferenceChainWindowExtraction {
  input: CharacterIndexInput
  calls: PreparedSingleWitnessCoreferenceChainWindowCall[]
  validationIssues: PreparedCharacterExtractionValidationIssue[]
  isValid: boolean
}

export interface PreparedLedgerSafeSingleWitnessCoreferenceChainV2WindowExtraction {
  input: CharacterIndexInput
  calls: PreparedSingleWitnessCoreferenceChainV2WindowCall[]
  validationIssues: PreparedCharacterExtractionValidationIssue[]
  isValid: boolean
}

export interface PreparedLedgerSafeSingleWitnessFocalizationCharacterizationExtraction {
  input: CharacterIndexInput
  call: PreparedSingleWitnessFocalizationCharacterizationExtractionCall
  validationIssues: PreparedCharacterExtractionValidationIssue[]
  isValid: boolean
}

export interface FocusedCoreferenceCharacterInput {
  characterId: string
  displayName: string
  aliases?: readonly string[]
  transliteration?: string | null
}

export interface FocusedCoreferenceOccurrenceInput {
  witnessSigla?: string
  rowLabel?: string
  label?: string
  summary?: string
  roleLabels?: readonly string[]
}

export interface BuildFocusedCoreferenceChainPromptArgs {
  character: FocusedCoreferenceCharacterInput
  currentOccurrences?: readonly FocusedCoreferenceOccurrenceInput[]
  sourceText: string
}

export interface BuildFocusedCoreferenceChainWindowCallsArgs {
  calls: readonly PreparedSingleWitnessCoreferenceChainWindowCall[]
  character: FocusedCoreferenceCharacterInput
  currentOccurrences?: readonly FocusedCoreferenceOccurrenceInput[]
}

const FOCUSED_COREFERENCE_CHAIN_PROMPT_VERSION_SUFFIX = 'focused-character-v1'

const normalizeLine = (value: unknown): string => (
  typeof value === 'string' ? value.trim().replace(/\s+/g, ' ') : ''
)

const uniqueStrings = (values: readonly unknown[]): string[] => (
  Array.from(new Set(values.map(normalizeLine).filter(Boolean)))
)

export const buildFocusedCoreferenceChainPromptText = ({
  character,
  currentOccurrences = [],
  sourceText,
}: BuildFocusedCoreferenceChainPromptArgs): string => {
  const aliases = uniqueStrings(character.aliases || [])
  const currentOccurrenceLines = currentOccurrences.slice(0, 24).map((occurrence, index) => {
    const locus = uniqueStrings([occurrence.witnessSigla, occurrence.rowLabel]).join(' ')
    const roles = uniqueStrings(occurrence.roleLabels || []).join(', ')
    const evidence = uniqueStrings([occurrence.label, occurrence.summary]).join(' :: ')
    return `${index + 1}. ${locus || 'current occurrence'}${roles ? ` [${roles}]` : ''}${evidence ? ` — ${evidence}` : ''}`
  })

  return [
    '# Focused coreference-chain extraction',
    '',
    'Improve the coreference chain for one existing character only.',
    '',
    '## Character',
    `- character_id: ${character.characterId}`,
    `- headword: ${character.displayName}`,
    `- aliases: ${aliases.length ? aliases.join('; ') : 'none'}`,
    `- transliteration: ${normalizeLine(character.transliteration) || 'none'}`,
    '',
    '## Current chain evidence',
    currentOccurrenceLines.length ? currentOccurrenceLines.join('\n') : 'No current occurrences are available.',
    '',
    '## Rules',
    '- Return one character entry only, for the character above.',
    '- Do not build a full character index and do not extract other characters as entries.',
    '- Include named mentions, epithets, role references, pronominal references, and zero-subject verbal references only when they resolve to this character in the supplied text.',
    '- Keep every `mention` and `context` grounded in the supplied text; use short verbatim context.',
    '- If a candidate mention is uncertain or could belong to another figure, omit it.',
    '- Do not invent text, names, rows, or characters.',
    '',
    '## Output',
    'Return a JSON object only:',
    '',
    '```json',
    '{ "characters": [{ "anchor": { "name": "", "transliteration": "" }, "chain": [{ "mention": "", "context": "", "host": "" }] }] }',
    '```',
    '',
    '## Text',
    sourceText,
  ].join('\n')
}

export const buildFocusedCoreferenceChainWindowCalls = ({
  calls,
  character,
  currentOccurrences = [],
}: BuildFocusedCoreferenceChainWindowCallsArgs): PreparedSingleWitnessCoreferenceChainWindowCall[] => (
  calls.map(call => ({
    ...call,
    promptText: buildFocusedCoreferenceChainPromptText({
      character,
      currentOccurrences,
      sourceText: call.sourceText,
    }),
    promptVersion: `${call.promptVersion}:${FOCUSED_COREFERENCE_CHAIN_PROMPT_VERSION_SUFFIX}`,
  }))
)

export const prepareLedgerSafeSingleWitnessCharacterExtraction = ({
  scope,
  ...inputArgs
}: PrepareLedgerSafeSingleWitnessCharacterExtractionArgs): PreparedLedgerSafeSingleWitnessCharacterExtraction => {
  const input = buildCharacterIndexInput(inputArgs)
  const call = prepareSingleWitnessCharacterExtractionCall(input, scope)
  const validationIssues = validatePreparedExtractionCall(call)
  return {
    input,
    call,
    validationIssues,
    isValid: validationIssues.length === 0,
  }
}

export const prepareLedgerSafeSingleWitnessCoreferenceChainExtraction = ({
  scope,
  ...inputArgs
}: PrepareLedgerSafeSingleWitnessCharacterExtractionArgs): PreparedLedgerSafeSingleWitnessCoreferenceChainExtraction => {
  const input = buildCharacterIndexInput(inputArgs)
  const call = prepareSingleWitnessCoreferenceChainExtractionCall(input, scope)
  const validationIssues = validatePreparedCoreferenceChainExtractionCall(call)
  return {
    input,
    call,
    validationIssues,
    isValid: validationIssues.length === 0,
  }
}

export const prepareLedgerSafeSingleWitnessCoreferenceChainWindowExtraction = ({
  scope,
  ...inputArgs
}: PrepareLedgerSafeSingleWitnessCharacterExtractionArgs): PreparedLedgerSafeSingleWitnessCoreferenceChainWindowExtraction => {
  const input = buildCharacterIndexInput(inputArgs)
  const calls = prepareSingleWitnessCoreferenceChainExtractionWindowCalls(input, scope)
  const validationIssues = calls.length === 0
    ? [{
      code: 'empty-source-text',
      message: 'No source rows are available for the selected coreference extraction scope.',
    } satisfies PreparedCharacterExtractionValidationIssue]
    : calls.flatMap(call => validatePreparedCoreferenceChainExtractionCall(call))
  return {
    input,
    calls,
    validationIssues,
    isValid: calls.length > 0 && validationIssues.length === 0,
  }
}

export const prepareLedgerSafeSingleWitnessCoreferenceChainV2WindowExtraction = ({
  scope,
  ...inputArgs
}: PrepareLedgerSafeSingleWitnessCharacterExtractionArgs): PreparedLedgerSafeSingleWitnessCoreferenceChainV2WindowExtraction => {
  const input = buildCharacterIndexInput(inputArgs)
  const calls = prepareSingleWitnessCoreferenceChainV2ExtractionWindowCalls(input, scope)
  const validationIssues = calls.length === 0
    ? [{
      code: 'empty-source-text',
      message: 'No source rows are available for the selected coreference v2 extraction scope.',
    } satisfies PreparedCharacterExtractionValidationIssue]
    : calls.flatMap(call => validatePreparedCoreferenceChainV2ExtractionCall(call))
  return {
    input,
    calls,
    validationIssues,
    isValid: calls.length > 0 && validationIssues.length === 0,
  }
}

export const prepareLedgerSafeSingleWitnessFocalizationCharacterizationExtraction = ({
  focusFigureId,
  figures,
  scope,
  ...inputArgs
}: PrepareLedgerSafeSingleWitnessFocalizationCharacterizationExtractionArgs): PreparedLedgerSafeSingleWitnessFocalizationCharacterizationExtraction => {
  const input = buildCharacterIndexInput(inputArgs)
  const call = prepareSingleWitnessFocalizationCharacterizationExtractionCall(input, scope, figures, {
    focusFigureId,
  })
  const validationIssues = figures.length === 0
    ? [{
      code: 'empty-source-text',
      message: 'Provide figure context before focalization.',
    } satisfies PreparedCharacterExtractionValidationIssue]
    : validatePreparedFocalizationCharacterizationExtractionCall(call)
  return {
    input,
    call,
    validationIssues,
    isValid: figures.length > 0 && validationIssues.length === 0,
  }
}
